import { globalStore } from '../store/store';

/**
 * Centralized utility function to determine if the current user should be treated as an admin.
 * This accounts for the "View as Non-Admin" toggle - when an admin has toggled to view as non-admin,
 * this function returns false even though they are actually an admin.
 * 
 * Use this function everywhere you need to check if the user should have admin privileges.
 * 
 * @returns {boolean} - true if user is an effective portal admin (is admin AND not viewing as non-admin)
 */
export const isEffectiveAdmin = () => {
    const state = globalStore.getState();
    const { isPortalAdmin, adminViewAsNonAdmin } = state.auth;
    return isPortalAdmin && !adminViewAsNonAdmin;
};

/**
 * Centralized utility function to determine if the current user should be treated as a support admin.
 * This accounts for the "View as Non-Admin" toggle.
 * 
 * @returns {boolean} - true if user is an effective support admin (is support admin AND not viewing as non-admin)
 */
export const isEffectiveSupportAdmin = () => {
    const state = globalStore.getState();
    const { isSupportAdmin, adminViewAsNonAdmin } = state.auth;
    return isSupportAdmin && !adminViewAsNonAdmin;
};

/**
 * Centralized utility function to determine if the current user should be treated as a corp reporting admin.
 * This accounts for the "View as Non-Admin" toggle.
 * 
 * @returns {boolean} - true if user is an effective corp reporting admin
 */
export const isEffectiveCorpReportingAdmin = () => {
    const state = globalStore.getState();
    const { isCorpReportingAdmin, adminViewAsNonAdmin } = state.auth;
    return isCorpReportingAdmin && !adminViewAsNonAdmin;
};

/**
 * Hook-friendly selectors for use in React components.
 * Use these with useSelector() to get reactive admin status that respects the toggle.
 */
export const selectIsEffectiveAdmin = (state) => {
    return state.auth.isPortalAdmin && !state.auth.adminViewAsNonAdmin;
};

export const selectIsEffectiveSupportAdmin = (state) => {
    return state.auth.isSupportAdmin && !state.auth.adminViewAsNonAdmin;
};

export const selectIsEffectiveCorpReportingAdmin = (state) => {
    return state.auth.isCorpReportingAdmin && !state.auth.adminViewAsNonAdmin;
};

export const selectAdminViewAsNonAdmin = (state) => {
    return state.auth.adminViewAsNonAdmin;
};

/**
 * Selector to check if the user is actually a real admin (ignores the toggle).
 * Use this only for showing/hiding the toggle itself.
 */
export const selectIsActualAdmin = (state) => {
    return state.auth.isPortalAdmin;
};
